// src/Advance.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import baseURL from './baseURl';
import logo from './logo.new1.png';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Advance = () => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const [receiptData, setReceiptData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cashAmount: '',
    pureWeight: '',
    type: 'given',
    advanceType: 'cash'
  });

  // Add a custom setter with logging
  const setCustomFormData = (updates) => {
    console.error('CRITICAL DEBUG - Updating form data:', JSON.stringify(updates, null, 2));
    
    // Intercept and correct any unexpected type
    if (updates.type === 'received') {
      console.error('CRITICAL DEBUG - Attempted to set invalid type "received"');
      updates.type = 'taken';
    }

    setFormData(prevData => {
      const newData = { ...prevData, ...updates };
      console.error('CRITICAL DEBUG - New form data:', JSON.stringify(newData, null, 2));
      return newData;
    });
  };

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientNotFound, setClientNotFound] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    let timer;
    if (showSuccessPopup) {
      timer = setTimeout(() => {
        setShowSuccessPopup(false);
        setMessage(null);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessPopup]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/clients`);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (err) {
      setError('Error fetching clients');
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCustomFormData({ name: searchValue });

    if (searchValue.trim() === '') {
      setFilteredClients(clients);
      setClientNotFound(false);
      return;
    }

    const filtered = clients.filter(client =>
      client.name.toLowerCase().includes(searchValue.toLowerCase())
    );

    setFilteredClients(filtered);
    setClientNotFound(filtered.length === 0);
  };

  const handleClientSelect = (clientName) => {
    setCustomFormData({ name: clientName });
    setSearchTerm(clientName);
    setFilteredClients([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      handleSearch(value);
    } else if (name === 'pureWeight') {
      // Allow entering digits, including decimal point
      // Limit to 3 decimal places
      const regex = /^\d*\.?\d{0,3}$/;
      if (regex.test(value)) {
        setCustomFormData({ [name]: value });
      }
    } else if (name === 'cashAmount') {
      // Allow entering digits, including decimal point
      // Limit to 2 decimal places
      const regex = /^\d*\.?\d{0,2}$/;
      if (regex.test(value) || value === '') {
        setCustomFormData({ [name]: value });
      }
    } else {
      setCustomFormData({ [name]: value });
    }
  };

  const resetForm = () => {
    setCustomFormData({
      name: '',
      cashAmount: '',
      pureWeight: '',
      type: 'given',
      advanceType: 'cash'
    });
    setSearchTerm('');
    setMessage(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setShowSuccessPopup(false);
    setReceiptData(null); // Reset receipt data

    // Comprehensive logging of form state before submission
    console.error('PRE-SUBMISSION FORM DATA:', JSON.stringify({
      name: formData.name,
      type: formData.type,
      advanceType: formData.advanceType,
      cashAmount: formData.cashAmount,
      pureWeight: formData.pureWeight
    }, null, 2));

    // Explicitly prevent 'received' from being used
    if (formData.type === 'received') {
      console.error('CRITICAL: Attempted to submit with "received" type');
      setError('Invalid advance type');
      return;
    }

    // Explicitly map to correct type
    const submitType = formData.type === 'taken' ? 'taken' : 'given';

    try {
      let endpoint;
      let payload;

      if (formData.advanceType === 'cash') {
        endpoint = `${baseURL}/api/transactions/cash-advance`;
        payload = {
          name: formData.name,
          cashAmount: parseFloat(formData.cashAmount),
          type: submitType
        };
      } else {
        endpoint = `${baseURL}/api/transactions/pure-weight-advance`;
        payload = {
          name: formData.name,
          pureWeight: parseFloat(formData.pureWeight),
          type: submitType
        };
      }

      // Final payload logging
      console.error('SUBMISSION PAYLOAD:', JSON.stringify(payload, null, 2));

            const response = await axios.post(endpoint, payload);

            // Generate receipt data
            setReceiptData({
            name: formData.name,
            type: submitType,
            advanceType: formData.advanceType,
            cashAmount: formData.cashAmount,
            pureWeight: formData.pureWeight,
            timestamp: new Date().toISOString()
            });

            setMessage(response.data.message || `${formData.advanceType === 'cash' ? 'Cash' : 'Pure Weight'} advance created successfully for ${formData.name}`);
            setShowSuccessPopup(true);
            resetForm(); // Clear form immediately

    } catch (error) {
      console.error('SUBMISSION ERROR:', JSON.stringify(error, null, 2));
      setError(error.response?.data?.message || 'An error occurred while creating the advance.');
    }
  };

  const handlePrintReceipt = () => {
    const doc = new jsPDF();
    doc.autoTable({
      columns: [
        { header: 'Date', dataKey: 'date' },
        { header: 'Client Name', dataKey: 'name' },
        { header: 'Transaction Type', dataKey: 'type' },
        { header: 'Advance Type', dataKey: 'advanceType' },
        { header: 'Amount', dataKey: 'amount' },
      ],
      body: [
        {
          date: formatDate(receiptData.timestamp),
          name: receiptData.name,
          type: receiptData.type,
          advanceType: receiptData.advanceType,
          amount: receiptData.advanceType === 'cash' ? `Rs. ${receiptData.cashAmount}` : `${receiptData.pureWeight} grams`,
        },
      ],
    });
    doc.save(`receipt_${receiptData.name}_${receiptData.timestamp}.pdf`);
  };

  const handleCloseReceipt = () => {
    setShowReceiptModal(false);
  };

  return (
    <div className="container mt-5">
      <h1>Advance Page</h1>

      {/* Receipt Modal */}
      {receiptData && showReceiptModal && (
        <div className="modal receipt-modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog">
          <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Receipt</h5>
            <button 
            type="button" 
            className="btn-close" 
            onClick={handleCloseReceipt}
            />
          </div>
          <div className="modal-body">
            <div className="receipt-content text-center">
            {/* Add Logo and Company Name */}
            <div className="mb-4">
              <img 
              src={logo} 
              alt="Company Logo" 
              style={{ width: '100px', height: 'auto' }}
              className="mb-2"
              />
              <h4 className="company-name">Speed Touch Gold</h4>
              <p className="company-address">
              123 Main Street, City<br />
              Phone: (123) 456-7890
              </p>
              <hr />
            </div>
            
              {/* Receipt Details */}
              <div className="text-start">
              <p><strong>Date:</strong> {formatDate(receiptData.timestamp)}</p>
              <p><strong>Client Name:</strong> {receiptData.name}</p>
              <p><strong>Transaction Type:</strong> {receiptData.type} {receiptData.advanceType} Advance</p>
              {receiptData.advanceType === 'cash' ? (
                <p><strong>Amount:</strong> Rs. {receiptData.cashAmount}</p>
              ) : (
                <p><strong>Pure Weight:</strong> {receiptData.pureWeight} grams</p>
              )}
              </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-top"></div>
              <p className="mb-0">Thank you for your business!</p>
              <small className="text-muted">This is a computer generated receipt</small>
            </div>
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="btn btn-secondary"
              onClick={handleCloseReceipt}
            >
              Close
            </button>
            <button 
              className="btn btn-primary"
              onClick={handlePrintReceipt}
              disabled={!receiptData}
            >
              {console.log('Receipt Data:', receiptData)}
              Print Receipt
            </button>
          </div>
          </div>
        </div>
       
      )}
    
      {/* Success Popup */}

    {showSuccessPopup && message && (
      <div 
        className="alert alert-success alert-dismissible fade show" 
        role="alert"
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1050,
          minWidth: '300px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          animation: 'slideIn 0.5s ease-out'
        }}
      >
        <strong>Success!</strong> {message}
        <button 
          type="button" 
          className="btn-close" 
          onClick={() => setShowSuccessPopup(false)}
        />
      </div>
    )}

    {/* Error Alert */}
    {error && (
      <div className="alert alert-danger alert-dismissible fade show" role="alert">
        {error}
        <button 
          type="button" 
          className="btn-close" 
          onClick={() => setError(null)}
        />
      </div>
    )}
    
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label">Advance Type</label>
        <div className="btn-group w-100">
          <button
            type="button"
            className={`btn ${formData.advanceType === 'cash' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setCustomFormData({ advanceType: 'cash' })}
          >
            Cash Advance
          </button>
          <button
            type="button"
            className={`btn ${formData.advanceType === 'pureWeight' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setCustomFormData({ advanceType: 'pureWeight' })}
          >
            Pure Weight Advance
          </button>
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">Advance Direction</label>
        <div className="btn-group w-100">
          <button
            type="button"
            className={`btn ${formData.type === 'taken' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setCustomFormData({ type: 'taken' })}
          >
            taken
          </button>
          <button
            type="button"
            className={`btn ${formData.type === 'given' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setCustomFormData({ type: 'given' })}
          >
            given
          </button>
        </div>
      </div>

      <div className="mb-3 position-relative">
        <label htmlFor="name" className="form-label">Client Name</label>
        <input
          type="text"
          id="name"
          name="name"
          className="form-control"
          value={searchTerm}
          onChange={handleChange}
          required
          autoComplete="off"
        />
        {searchTerm && filteredClients.length > 0 && (
          <ul className="list-group position-absolute w-100" style={{ zIndex: 1000 }}>
            {filteredClients.map((client, index) => (
              <li
                key={index}
                className="list-group-item list-group-item-action"
                onClick={() => handleClientSelect(client.name)}
                style={{ cursor: 'pointer' }}
              >
                {client.name}
              </li>
            ))}
          </ul>
        )}
        {clientNotFound && searchTerm && (
          <div className="text-danger mt-1">Client not found</div>
        )}
      </div>

      {formData.advanceType === 'cash' ? (
        <div className="mb-3">
          <label htmlFor="cashAmount" className="form-label">Cash Amount</label>
          <input
            type="number"
            id="cashAmount"
            name="cashAmount"
            className="form-control"
            value={formData.cashAmount}
            onChange={handleChange}
            required
            min="0"
            step="0.01"
          />
        </div>
      ) : (
        <div className="mb-3">
          <label htmlFor="pureWeight" className="form-label">Pure Weight</label>
          <input
              type="number"
              id="pureWeight"
              name="pureWeight"
              className="form-control"
              value={formData.pureWeight}
              onChange={handleChange}
              required
              min="0"
              step="0.001"
            />
            </div>
          )}

          <button type="submit" className="btn btn-primary">Create Advance</button>
          </form>

          <style jsx>{`
            @media print {
              body * {
                visibility: hidden;
              }
              .receipt-modal, 
              .receipt-modal * {
                visibility: visible;
              }
              .receipt-modal {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
              }
              .modal-dialog {
                max-width: 100%;
                width: 100%;
              }
              .modal-content {
                border: none;
                box-shadow: none;
              }
            }
            @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
            }
            .modal-backdrop {
            display: none;
            }
          `}</style>
        </div>
        );
      };

      export default Advance;
